/**
 * =============================================================================
 * PASSIVE TREE - Tipos y definiciones para el árbol de habilidades pasivas
 * =============================================================================
 * 
 * Sistema inspirado en Path of Exile donde cada raza comienza en un punto
 * diferente del árbol y puede especializarse en diferentes direcciones.
 */

import type { CharacterRaceId } from './character';

// =============================================================================
// TIPOS DE NODOS
// =============================================================================

/**
 * Tipo de nodo en el árbol de pasivas
 */
export type PassiveNodeType = 'small' | 'notable' | 'keystone';

/**
 * Tipo de estadística que puede modificar un nodo
 */
export type PassiveStatType =
  // ─── Flat / recursos ──────────────────────────────────────────────────────
  | 'maxHp'           // Vida máxima
  | 'maxMana'         // Maná máximo
  | 'maxStamina'      // Stamina máxima
  | 'physDef'         // Defensa física
  | 'magicDef'        // Defensa mágica
  | 'minDamage'       // Daño mínimo
  | 'maxDamage'       // Daño máximo
  | 'critChance'      // Probabilidad de crítico (%)
  | 'critMultiplier'  // Multiplicador de crítico (flat decimal, +0.2 = +20% del golpe)
  | 'attackSpeed'     // Velocidad de ataque (%)
  | 'moveSpeed'       // Velocidad de movimiento (%)
  | 'evasion'         // Evasión (%)
  | 'regenHp'         // Regeneración de HP/s
  | 'regenMana'       // Regeneración de Maná/s
  | 'regenStamina'    // Regeneración de Stamina/s
  | 'manaReduction'   // Reducción de costo de maná (%)
  | 'castSpeed'       // Velocidad de casteo (%)
  | 'blockChance'     // Probabilidad de bloqueo físico (%)
  | 'lifeLeech'       // Robo de vida físico (%)
  | 'manaLeech'       // Robo de maná al golpear (%)
  // ─── Porcentuales de recurso / daño (%) ───────────────────────────────────
  | 'hpPercent'               // +% HP máximo
  | 'manaPercent'             // +% Maná máximo
  | 'staminaPercent'          // +% Stamina máxima
  | 'damagePercent'           // +% daño (min y max)
  | 'meleeDamagePercent'      // +% daño físico melee
  | 'projectileDamagePercent' // +% daño físico a distancia (arco/ballesta/arrojadiza)
  | 'spellDamagePercent'      // +% daño de hechizos
  // ─── Sustain / robo / reflect ─────────────────────────────────────────────
  | 'lifeStealPhysical'       // Robo de vida con daño físico (%)
  | 'lifeStealMagic'          // Robo de vida con daño mágico (%)
  | 'reflectPhysicalDamage'   // Refleja daño físico recibido (%)
  | 'reflectMagicDamage'      // Refleja daño mágico recibido (%)
  | 'healingReceived'         // +% a curación recibida
  | 'healingOutput'           // +% a curación que hacés a otros
  | 'magicFind'               // +% chance de drops raros/épicos
  // ─── Control de masas (ofensivo/defensivo) ────────────────────────────────
  | 'impairImmunity'              // FLAG: inmune a inmovilizar/paralizar
  | 'immobilizeOnHitPhysical'     // Chance de inmovilizar con golpe físico (%)
  | 'immobilizeOnHitSpell'        // Chance de inmovilizar con hechizo (%)
  | 'immobilizeDurationReduction' // -% duración de inmovilizar recibido
  | 'paralyzeDurationReduction'   // -% duración de paralizar recibido
  // ─── Invocaciones ─────────────────────────────────────────────────────────
  | 'summonHpBonus'               // +HP flat a invocaciones
  | 'summonPhysicalDamageBonus'   // +daño físico flat a invocaciones
  | 'summonMagicDamageBonus'      // +daño mágico flat a invocaciones
  | 'summonAttackSpeedBonus'      // +% vel. ataque de invocaciones
  | 'summonCritChance'            // +% prob. de crítico de invocaciones
  | 'summonCritMultiplier'        // +% multiplicador de crítico de invocaciones
  | 'summonResurrectionSpeed'     // -% tiempo de resurrección de invocaciones permanentes
  // ─── Utilidad / hechizos ──────────────────────────────────────────────────
  | 'spellRangeBonus'             // +tiles al rango de hechizos (flat)
  | 'attackRangeLength'           // +tiles al largo del radio de ataque (flat)
  // ─── Skilling / extracción ────────────────────────────────────────────────
  | 'miningSuccessBonus'          // +% multiplicativo a la prob. de éxito al minar (100 = duplica)
  // ─── Maestría de arma: +% daño físico con un tipo de arma (%) ─────────────
  | 'damageWithSword'
  | 'damageWithAxe'
  | 'damageWithMace'
  | 'damageWithDagger'
  | 'damageWithKnuckles'
  | 'damageWithBow'
  | 'damageWithCrossbow'
  | 'damageWithThrowable'
  // ─── Flags únicos ─────────────────────────────────────────────────────────
  | 'alwaysCrit'                  // FLAG: todos los golpes melee son críticos
  | 'backstabCritDagger'          // FLAG: crítico garantizado al golpear la espalda con DAGAS
  | 'blockOmnidirectional'        // FLAG: podés bloquear golpes de cualquier dirección
  | 'invisibilityNeverExpires'    // FLAG: la invisibilidad no expira (se rompe solo al atacar)
  | 'silentWhileInvisible'        // FLAG: invisible = sin huellas ni ruido al caminar
  | 'seeInvisible'                // FLAG: ves a jugadores invisibles (silueta + nombre)
  | 'critDamageReduction'         // -fracción del BONUS de daño crítico (0.5 = la mitad)
  | 'noStaminaCostPhysical'       // FLAG: atacar no consume stamina
  | 'noShield'                    // FLAG: no puede equipar escudo
  | 'noMeditate'                  // FLAG: no puede meditar
  | 'periodicManaRegenPercent';   // % de maná máx restaurado cada 5s (sustituto de meditar)

/**
 * Modificador de estadística de un nodo
 */
export interface PassiveStatModifier {
  stat: PassiveStatType;
  value: number;           // Valor del modificador (puede ser % o flat según el stat)
  isPercentage?: boolean;  // Si es porcentaje (para mostrar en UI)
}

// =============================================================================
// NODOS DEL ÁRBOL
// =============================================================================

/**
 * Definición de un nodo en el árbol de pasivas
 */
export interface PassiveNode {
  /** ID único del nodo (ej: "dwarf_start", "str_1", "keystone_iron_will") */
  id: string;
  
  /** Nombre para mostrar */
  name: string;
  
  /** Descripción del nodo */
  description: string;
  
  /** Tipo de nodo (afecta el tamaño visual) */
  type: PassiveNodeType;
  
  /** Posición X en el canvas del editor (píxeles) */
  x: number;
  
  /** Posición Y en el canvas del editor (píxeles) */
  y: number;
  
  /** IDs de nodos conectados (bidireccional) */
  connections: string[];
  
  /** Modificadores de estadísticas que otorga este nodo */
  modifiers: PassiveStatModifier[];
  
  /** Afinidad de raza (null = neutral/centro) */
  raceAffinity: CharacterRaceId | 'gnome' | null;
  
  /** Si es un punto de inicio de raza */
  isStartingPoint?: boolean;
  
  /** Icono opcional para keystones (nombre del asset) */
  icon?: string;
}

// =============================================================================
// ESTRUCTURA DEL ÁRBOL COMPLETO
// =============================================================================

/**
 * Definición completa del árbol de pasivas
 */
export interface PassiveTreeData {
  /** Versión del formato (para migraciones futuras) */
  version: number;
  
  /** Nombre del árbol */
  name: string;
  
  /** Todos los nodos del árbol */
  nodes: PassiveNode[];
  
  /** Mapeo de raza -> ID del nodo de inicio */
  startingPoints: Record<CharacterRaceId | 'gnome', string>;
  
  /** Centro del árbol (para cámara inicial) */
  center: { x: number; y: number };
  
  /** Bounds del árbol para límites de cámara */
  bounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
}

// =============================================================================
// ESTADO DEL JUGADOR
// =============================================================================

/**
 * Nodos asignados por un jugador
 */
export interface PlayerPassiveAllocation {
  /** ID del personaje */
  characterId: string;
  
  /** IDs de nodos asignados */
  allocatedNodes: string[];
  
  /** Puntos disponibles para gastar */
  availablePoints: number;
  
  /** Puntos totales gastados */
  spentPoints: number;
}

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * Tamaños de nodos en píxeles (radio)
 */
export const PASSIVE_NODE_SIZES: Record<PassiveNodeType, number> = {
  small: 11, // menor: el más chico
  notable: 17, // notable: mediano
  keystone: 24, // clave (con icono): el más grande
};

/**
 * Colores por afinidad de raza
 */
export const PASSIVE_RACE_COLORS: Record<CharacterRaceId | 'gnome' | 'neutral', number> = {
  dwarf: 0x888888,     // Gris
  elf: 0xFFD700,       // Amarillo/Dorado
  drow: 0x1E3A5F,      // Azul oscuro
  gnome: 0x87CEEB,     // Celeste
  human: 0xF5F5F5,     // Blanco
  orc: 0x228B22,       // Verde
  neutral: 0x4a4a4a,   // Gris neutro para centro
};

/**
 * Colores de borde para estados de nodos
 */
export const PASSIVE_NODE_BORDER_COLORS = {
  available: 0xaaaaaa,    // Gris claro - puede seleccionarse
  allocated: 0xFFD700,    // Dorado - ya asignado
  locked: 0x333333,       // Gris oscuro - no conectado
  hover: 0xffffff,        // Blanco - hover
};

/**
 * Niveles por punto de pasiva.
 * El jugador gana 1 punto cada LEVELS_PER_PASSIVE_POINT niveles. Con nivel máx 50
 * y ratio 2 → 25 puntos totales, suficiente para recorrer la rama racial propia
 * y asomarse a una vecina, pero nunca todo el árbol. Ver docs/SKILL_TREE.md.
 */
export const LEVELS_PER_PASSIVE_POINT = 2;

/**
 * Posiciones iniciales de los puntos de inicio por raza
 * Distribuidos en un hexágono alrededor del centro
 */
export const RACE_STARTING_POSITIONS: Record<CharacterRaceId | 'gnome', { x: number; y: number; angle: number }> = {
  dwarf:  { x: 0,    y: -300, angle: 270 },  // Arriba (12:00)
  elf:    { x: 260,  y: -150, angle: 330 },  // Derecha superior (2:00)
  drow:   { x: 260,  y: 150,  angle: 30 },   // Derecha inferior (4:00)
  gnome:  { x: 0,    y: 300,  angle: 90 },   // Abajo (6:00)
  human:  { x: -260, y: -150, angle: 210 },  // Izquierda superior (10:00)
  orc:    { x: -260, y: 150,  angle: 150 },  // Izquierda inferior (8:00)
};

/**
 * Centro del árbol (donde convergen todas las razas)
 */
export const PASSIVE_TREE_CENTER = { x: 500, y: 500 };

/**
 * Radio del primer anillo común (donde se unen las 6 razas)
 */
export const FIRST_RING_RADIUS = 120;
